/**
 * DisclosureTierSwitch (D3 · Instrument 14, chrome-adjacent)
 *
 * Three-tier segmented control: NOVICE / INTERMEDIATE / FORENSIC. Controlled
 * component only — does not own state. The hoisted shared state hook is
 * authored at Session 1A as part of the AmbientGovernanceStrip wiring; the
 * Strip + AppShell + this Switch all read the same instance per
 * P2.D7 §2.1 read 4.
 *
 * Constitutional basis:
 *   - H-AX-02 (Clarity): tier selection is explicit and persistent.
 *   - V-AX-02 (Structure): single source of truth — the canonical tier value
 *     is hoisted to app-shell scope, never duplicated per-zone.
 *
 * Spec: SN-FE-003 / Stage 1 / D3 · Instrument 14
 *       SN-FE-003-Stage4-P2D7-StripActivation §2.1 read 4.
 *       D9 specifies what each tier renders; this is the affordance.
 */

import React, { useRef } from 'react';

type DisclosureTier = 'novice' | 'intermediate' | 'forensic';

interface DisclosureTierSwitchProps {
  value: DisclosureTier;
  onChange: (next: DisclosureTier) => void;
  disabled?: boolean;
}

const TIERS: ReadonlyArray<{ id: DisclosureTier; label: string }> = [
  { id: 'novice', label: 'NOVICE' },
  { id: 'intermediate', label: 'INTERMEDIATE' },
  { id: 'forensic', label: 'FORENSIC' },
];

export function DisclosureTierSwitch({
  value,
  onChange,
  disabled = false,
}: DisclosureTierSwitchProps) {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (disabled) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (index + direction + TIERS.length) % TIERS.length;
      const nextTier = TIERS[nextIndex];
      onChange(nextTier.id);
      buttonRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div
      data-testid="disclosure-tier-switch"
      role="group"
      aria-label="Disclosure tier"
      style={{
        display: 'inline-flex',
        gap: '0',
        padding: '2px',
        background: 'var(--sn-depth-2)',
        border: '1px solid var(--sn-border-subtle)',
        borderRadius: '2px',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {TIERS.map((tier, index) => {
        const active = value === tier.id;
        return (
          <button
            key={tier.id}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            data-testid={`disclosure-tier-${tier.id}`}
            data-active={active}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onChange(tier.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            style={{
              padding: '4px 12px',
              fontFamily: 'var(--sn-font-data)',
              fontSize: '11px',
              letterSpacing: '0.08em',
              // Inactive label uses the canonical dark-surface muted token
              // (--sn-depth-text-secondary) rather than the light-theme
              // --sn-depth-text-tertiary: the switch background is the hardcoded dark
              // --sn-depth-2, on which --sn-depth-text-tertiary (#6B6860) is only ~3.2:1.
              // --sn-depth-text-secondary (#A09D95) clears WCAG AA (4.5:1) there.
              color: active
                ? 'var(--sn-depth-text-primary)'
                : 'var(--sn-depth-text-secondary)',
              background: active
                ? 'hsl(216, 100%, 33%)'
                : 'transparent',
              border: 'none',
              borderRadius: '2px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'background var(--sn-motion-swift) var(--sn-ease-swift), color var(--sn-motion-swift) var(--sn-ease-swift)',
            }}
          >
            {tier.label}
          </button>
        );
      })}
    </div>
  );
}
